import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, updateDoc, collectionGroup, query, where, getDocs, collection, increment } from 'firebase/firestore';
import { Check, X, Clock, Utensils, ChevronRight, MapPin, CircleAlert, Pizza, Coffee, Soup, Salad } from 'lucide-react';
import clsx from 'clsx';

export const Confirmation = () => {
    const { sessionId, playerId } = useParams();
    const navigate = useNavigate();

    const [session, setSession] = useState(null);
    const [hostId, setHostId] = useState(null);
    const [player, setPlayer] = useState(null);
    const [restaurants, setRestaurants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('idle'); // 'idle', 'confirming', 'late', 'meal', 'done'
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Find session across all hosts
                const sessionsQuery = query(collectionGroup(db, 'sessions'), where('gameId', '==', sessionId.replace('session_', '')));
                const snap = await getDocs(sessionsQuery);
                
                if (snap.empty) {
                    setError("הסשן לא נמצא.");
                    setLoading(false);
                    return;
                }

                const sDoc = snap.docs[0];
                const sData = sDoc.data();
                setSession(sData);
                const hId = sDoc.ref.parent.parent.id;
                setHostId(hId);

                // Find player
                const pRef = doc(db, `hosts/${hId}/players/${playerId}`);
                const pSnap = await getDoc(pRef);
                if (pSnap.exists()) {
                    setPlayer({ id: pSnap.id, ...pSnap.data() });
                } else {
                    setError("השחקן לא נמצא.");
                }

                // If meal selection is active, fetch attached restaurants
                if (sData.confirmationType === 'meal' && sData.attachedRestaurants?.length > 0) {
                    const resList = [];
                    for (const resId of sData.attachedRestaurants) {
                        const rRef = doc(db, `hosts/${hId}/restaurants/${resId}`);
                        const rSnap = await getDoc(rRef);
                        if (rSnap.exists()) resList.push({ id: rSnap.id, ...rSnap.data() });
                    }
                    setRestaurants(resList);
                }
            } catch (err) {
                console.error(err);
                setError("אירעה שגיאה בטעינת הנתונים.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [sessionId, playerId]);

    const handleConfirmArrival = async () => {
        if (!hostId || !session) return;
        setSubmitting(true);
        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/session_${session.gameId}`);
            const registered_players = session.registered_players || {};
            if (registered_players[playerId]) {
                registered_players[playerId].status = 'confirmed';
            }
            await updateDoc(sessionRef, { registered_players });
            setStatus('done');
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCantMakeIt = async () => {
        if (!hostId || !session) return;
        if (!window.confirm("בטוח שאתה לא יכול להגיע? השולחן ממתין לך.")) return;
        setSubmitting(true);
        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/session_${session.gameId}`);
            const registered_players = session.registered_players || {};
            if (registered_players[playerId]) {
                registered_players[playerId].status = 'cancelled';
            }
            await updateDoc(sessionRef, { registered_players });
            setStatus('done');
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleLate = async (minutes) => {
        if (!hostId || !session) return;
        setSubmitting(true);
        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/session_${session.gameId}`);
            const registered_players = session.registered_players || {};
            if (registered_players[playerId]) {
                registered_players[playerId].status = 'late';
                registered_players[playerId].estimatedArrival = minutes;
            }
            await updateDoc(sessionRef, { registered_players });
            
            // Update player tardiness score
            const playerRef = doc(db, `hosts/${hostId}/players/${playerId}`);
            await updateDoc(playerRef, { tardinessScore: increment(minutes) });
            
            setStatus('done');
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleMealSelect = async (restaurantName, itemName) => {
        if (!hostId || !session) return;
        setSubmitting(true);
        try {
            const sessionRef = doc(db, `hosts/${hostId}/sessions/session_${session.gameId}`);
            const registered_players = session.registered_players || {};
            const meal_orders = session.meal_orders || {};

            if (registered_players[playerId]) {
                registered_players[playerId].status = 'confirmed';
                registered_players[playerId].mealChoice = { restaurantName, itemName };
            }

            meal_orders[playerId] = {
                playerName: player?.name,
                restaurantName,
                itemName,
                timestamp: new Date().toISOString()
            };

            await updateDoc(sessionRef, { registered_players, meal_orders });
            setStatus('done');
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center"><div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
    
    if (error) return <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-500 font-heebo p-6 text-center">{error}</div>;

    if (status === 'done') {
        return (
            <div className="min-h-screen bg-slate-950 text-white p-6 flex flex-col items-center justify-center font-heebo text-center" dir="rtl">
                <div className="w-20 h-20 bg-emerald-500/20 border border-emerald-500/30 rounded-full flex items-center justify-center mb-6 animate-in zoom-in duration-500">
                    <Check className="w-10 h-10 text-emerald-400" />
                </div>
                <h1 className="text-3xl font-black mb-2">עודכן בהצלחה!</h1>
                <p className="text-slate-400 mb-8 max-w-xs">{player?.name}, העדכון שלך הועבר למארח. נתראה בשולחן!</p>
            </div>
        );
    }

    if (status === 'late') {
        const options = [
            { label: '15 דק׳', value: 15 },
            { label: '30 דק׳', value: 30 },
            { label: '45 דק׳', value: 45 },
            { label: 'שעה', value: 60 },
            { label: 'שעה וחצי', value: 90 },
            { label: 'שעתיים', value: 120 },
            { label: '2 שעות +', value: 150 },
        ];
        return (
            <div className="min-h-screen bg-slate-950 text-white p-6 flex flex-col font-heebo" dir="rtl">
                <button onClick={() => setStatus('idle')} className="flex items-center gap-2 text-slate-500 hover:text-white mb-8">
                    <ArrowLeft className="w-4 h-4 rotate-180" /> חזרה
                </button>
                <h1 className="text-2xl font-black text-amber-500 mb-2">בכמה אתה מאחר?</h1>
                <p className="text-slate-400 text-sm mb-8 italic">שימו לב: איחורים נרשמים בפרופיל השחקן.</p>
                <div className="grid grid-cols-1 gap-3">
                    {options.map(opt => (
                        <button
                            key={opt.value}
                            disabled={submitting}
                            onClick={() => handleLate(opt.value)}
                            className="bg-slate-900 border border-white/5 rounded-2xl py-5 font-black text-lg hover:border-amber-500/50 transition-all flex items-center justify-center gap-3 active:scale-[0.98]"
                        >
                            <Clock className="w-5 h-5 text-amber-500" />
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    if (status === 'meal') {
        return (
            <div className="min-h-screen bg-slate-950 text-white p-6 flex flex-col font-heebo" dir="rtl">
                <button onClick={() => setStatus('idle')} className="flex items-center gap-2 text-slate-500 hover:text-white mb-8">
                    <ArrowLeft className="w-4 h-4 rotate-180" /> חזרה
                </button>
                <h1 className="text-2xl font-black text-amber-500 mb-2">מה תרצו לאכול?</h1>
                <p className="text-slate-400 text-sm mb-8 leading-relaxed">בחירת המנה מהווה אישור הגעה סופית לסשן.</p>
                
                <div className="space-y-6">
                    {restaurants.map(res => (
                        <div key={res.id} className="space-y-3">
                            <div className="flex items-center gap-2 text-amber-500/70">
                                <Utensils className="w-4 h-4" />
                                <h3 className="text-xs font-black uppercase tracking-widest">{res.name}</h3>
                            </div>
                            <div className="space-y-3">
                                {res.menu?.map((item, idx) => (
                                    <button
                                        key={idx}
                                        disabled={submitting}
                                        onClick={() => handleMealSelect(res.name, item.itemName)}
                                        className="w-full bg-slate-900/60 border border-white/5 rounded-2xl p-4 text-right hover:border-amber-500/50 transition-all group flex items-start justify-between"
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h4 className="font-bold text-white group-hover:text-amber-500 transition-colors">{item.itemName}</h4>
                                                <span className="text-[10px] bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded">₪{item.price}</span>
                                            </div>
                                            {item.description && <p className="text-[10px] text-slate-500 leading-relaxed font-light">{item.description}</p>}
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-amber-500 transition-colors mt-1" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-white p-6 flex flex-col items-center justify-center font-heebo" dir="rtl">
            <div className="w-full max-w-sm mb-12 text-center">
                <div className="w-16 h-16 bg-amber-500/10 border border-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Utensils className="w-8 h-8 text-amber-500" />
                </div>
                <h1 className="text-2xl font-black mb-2">{player?.name}, אתה מגיע?</h1>
                <p className="text-sm text-slate-500 px-8">אנא אשרו הגעה כדי שנוכל להיערך בהתאם.</p>
            </div>

            <div className="w-full max-w-sm space-y-4">
                <button
                    onClick={session.confirmationType === 'meal' ? () => setStatus('meal') : handleConfirmArrival}
                    disabled={submitting}
                    className="w-full group bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] transition-all p-8 rounded-3xl flex flex-col items-center justify-center gap-3 shadow-xl shadow-emerald-500/10"
                >
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Check className="w-8 h-8 text-white" />
                    </div>
                    <span className="text-xl font-black text-slate-950">
                        {session.confirmationType === 'meal' ? 'אשר הגעה ובחר מנה' : 'אני מגיע'}
                    </span>
                </button>

                <div className="grid grid-cols-2 gap-4">
                    <button
                        onClick={() => setStatus('late')}
                        disabled={submitting}
                        className="bg-slate-900 border border-white/5 hover:border-amber-500/40 p-6 rounded-3xl flex flex-col items-center gap-3 transition-all active:scale-[0.95]"
                    >
                        <Clock className="w-6 h-6 text-amber-500" />
                        <span className="text-xs font-bold">מאחר קצת</span>
                    </button>
                    <button
                        onClick={handleCantMakeIt}
                        disabled={submitting}
                        className="bg-slate-900 border border-white/5 hover:border-rose-500/40 p-6 rounded-3xl flex flex-col items-center gap-3 transition-all active:scale-[0.95]"
                    >
                        <X className="w-6 h-6 text-rose-500" />
                        <span className="text-xs font-bold">לא יכול להגיע</span>
                    </button>
                </div>
            </div>
            
            <div className="mt-12 text-[10px] text-slate-600 font-medium flex items-center gap-2">
                <CircleAlert className="w-3 h-3" />
                <span>ניתן לעדכן את הבחירה עד שעה לפני תחילת המשחק.</span>
            </div>
        </div>
    );
};

const ArrowLeft = ({ className }) => (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12"></line>
        <polyline points="12 19 5 12 12 5"></polyline>
    </svg>
);
